export type PointValue = 0 | 15 | 30 | 40;

export interface GameScore {
  player: PointValue | 'Ad';
  opponent: PointValue | 'Ad';
}

export interface SetScore {
  player: number;
  opponent: number;
}

export interface MatchState {
  gameScore: GameScore;
  sets: SetScore[];
  currentSet: SetScore;
  isDeuce: boolean;
  matchOver: boolean;
  winner: 'player' | 'opponent' | null;
  pointsPlayed: number;
}

const PROGRESSION: PointValue[] = [0, 15, 30, 40];

export const initMatchState = (): MatchState => ({
  gameScore: { player: 0, opponent: 0 },
  sets: [],
  currentSet: { player: 0, opponent: 0 },
  isDeuce: false,
  matchOver: false,
  winner: null,
  pointsPlayed: 0,
});

const nextPoint = (current: PointValue | 'Ad'): PointValue | 'Ad' => {
  if (current === 'Ad') return 40;
  const idx = PROGRESSION.indexOf(current as PointValue);
  return idx === -1 ? 0 : (PROGRESSION[idx + 1] ?? 40);
};

const isGameWon = (winner: 'player' | 'opponent', state: GameScore): boolean => {
  const wScore = state[winner];
  const loser = winner === 'player' ? 'opponent' : 'player';
  const lScore = state[loser];
  if (wScore === 'Ad') return true;
  if (wScore === 40 && lScore !== 40 && lScore !== 'Ad') return true;
  return false;
};

const isSetOver = (set: SetScore): { over: boolean; winner: 'player' | 'opponent' | null } => {
  const { player, opponent } = set;
  if (player >= 6 && player - opponent >= 2) return { over: true, winner: 'player' };
  if (opponent >= 6 && opponent - player >= 2) return { over: true, winner: 'opponent' };
  if (player === 7 || opponent === 7) {
    return { over: true, winner: player > opponent ? 'player' : 'opponent' };
  }
  return { over: false, winner: null };
};

const isMatchOver = (sets: SetScore[]): 'player' | 'opponent' | null => {
  let pSets = 0, oSets = 0;
  sets.forEach(s => {
    if (s.player > s.opponent) pSets++;
    else oSets++;
  });
  if (pSets >= 2) return 'player';
  if (oSets >= 2) return 'opponent';
  return null;
};

export const applyPoint = (
  state: MatchState,
  winner: 'player' | 'opponent'
): MatchState => {
  if (state.matchOver) return state;

  let gs = { ...state.gameScore };
  const loser = winner === 'player' ? 'opponent' : 'player';

  const newState: MatchState = {
    ...state,
    gameScore: { ...gs },
    sets: [...state.sets],
    currentSet: { ...state.currentSet },
    pointsPlayed: state.pointsPlayed + 1,
  };

  // Deuce logic
  if (gs.player === 40 && gs.opponent === 40) {
    // At deuce, give advantage
    newState.gameScore[winner] = 'Ad';
    newState.isDeuce = false;
    return newState;
  }

  if (gs[winner] === 'Ad') {
    // Win the game
    newState.gameScore = { player: 0, opponent: 0 };
    newState.isDeuce = false;
    newState.currentSet = { ...state.currentSet };
    newState.currentSet[winner] += 1;
    // Check set
    const setResult = isSetOver(newState.currentSet);
    if (setResult.over) {
      newState.sets = [...state.sets, { ...newState.currentSet }];
      newState.currentSet = { player: 0, opponent: 0 };
      const matchWinner = isMatchOver(newState.sets);
      if (matchWinner) {
        newState.matchOver = true;
        newState.winner = matchWinner;
      }
    }
    return newState;
  }

  if (gs[loser] === 'Ad') {
    // Back to deuce
    newState.gameScore = { player: 40, opponent: 40 };
    newState.isDeuce = true;
    return newState;
  }

  // Normal point progression
  const newScore = nextPoint(gs[winner] as PointValue);
  newState.gameScore[winner] = newScore;

  if (isGameWon(winner, newState.gameScore)) {
    newState.gameScore = { player: 0, opponent: 0 };
    newState.isDeuce = false;
    newState.currentSet[winner] += 1;
    const setResult = isSetOver(newState.currentSet);
    if (setResult.over) {
      newState.sets = [...state.sets, { ...newState.currentSet }];
      newState.currentSet = { player: 0, opponent: 0 };
      const matchWinner = isMatchOver(newState.sets);
      if (matchWinner) {
        newState.matchOver = true;
        newState.winner = matchWinner;
      }
    }
    // Check deuce
  } else if (newState.gameScore.player === 40 && newState.gameScore.opponent === 40) {
    newState.isDeuce = true;
  }

  return newState;
};

export const formatScore = (score: PointValue | 'Ad'): string => {
  if (score === 'Ad') return 'Ad';
  return String(score);
};
